import { env } from '@/shared/config/env'

import { ApiError, apiErrorFromResponse, NetworkError } from './api-error'
import { IDEMPOTENT_OPERATIONS } from './idempotency'

type QueryValue = string | number | boolean | null | undefined
type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** MAX_BODY_BYTES do backend (413 payload_too_large). */
const MAX_BODY_BYTES = 1_048_576

/**
 * `?: T | undefined` (e não só `?: T`): com exactOptionalPropertyTypes, um `?`
 * puro recusa `undefined` EXPLÍCITO, e estas são opções de repasse — quem chama
 * `api.get(path, query)` legitimamente passa `query: undefined`. O `| undefined`
 * diz "pode faltar OU vir undefined", que é a intenção real.
 */
export interface RequestOptions {
  readonly method?: Method | undefined
  readonly body?: unknown
  readonly query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>> | undefined
  /** Obrigatório nas 8 rotas de efeito financeiro. Minte por INTENÇÃO (useIdempotencyKey). */
  readonly idempotencyKey?: string | undefined
  /** Template do path para a checagem da allowlist, ex.: '/v1/schedules/{schedule_id}/settle'. */
  readonly operationPath?: string | undefined
  readonly signal?: AbortSignal | undefined
}

export interface ApiResult<T> {
  readonly data: T
  readonly status: number
  /** 201 = efeito NOVO. 200 num POST idempotente = REPLAY (docs/idempotencia.md). */
  readonly created: boolean
}

// --- injeção de token: setada no bootstrap de auth. Evita ciclo http ⇄ store. ---
type TokenProvider = () => Promise<string | null>
let tokenProvider: TokenProvider = () => Promise.resolve(null)
let onUnauthorized: () => void = () => undefined

export function configureHttp(opts: { getToken: TokenProvider; onUnauthorized: () => void }): void {
  tokenProvider = opts.getToken
  onUnauthorized = opts.onUnauthorized
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url = `${base}${path}`
  if (!query) return url
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue
    for (const item of Array.isArray(v) ? v : [v]) {
      if (item !== undefined && item !== null) sp.append(k, String(item))
    }
  }
  const qs = sp.toString()
  return qs ? `${url}?${qs}` : url
}

export async function requestFull<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<ApiResult<T>> {
  const method = opts.method ?? 'GET'
  const opKey = `${method} ${opts.operationPath ?? path}`

  if (import.meta.env.DEV) {
    // Falha ANTES do round-trip, com a causa real — em vez de um 400 opaco lá na
    // frente, ou (pior) de uma chave silenciosamente ignorada.
    if (IDEMPOTENT_OPERATIONS.has(opKey) && !opts.idempotencyKey) {
      throw new Error(`[http] ${opKey} exige Idempotency-Key. Use useIdempotencyKey().`)
    }
    if (!IDEMPOTENT_OPERATIONS.has(opKey) && opts.idempotencyKey) {
      throw new Error(
        `[http] ${opKey} NÃO aceita Idempotency-Key (o backend ignora — provável erro de chamada).`,
      )
    }
  }

  const headers = new Headers({ Accept: 'application/json' })
  const token = await tokenProvider()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json')
  if (opts.idempotencyKey) headers.set('Idempotency-Key', opts.idempotencyKey)

  const serialized = opts.body === undefined ? undefined : JSON.stringify(opts.body)
  if (serialized !== undefined && new Blob([serialized]).size > MAX_BODY_BYTES) {
    // Reporta local, sem gastar a viagem só para receber 413.
    throw new ApiError(413, 'payload_too_large', 'Corpo da requisição excede 1 MiB.')
  }

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      // `?? null` e não `serialized`: com exactOptionalPropertyTypes, `undefined`
      // não é atribuível a `body?: BodyInit | null`. O fetch trata null como
      // "sem corpo", que é a intenção.
      body: serialized ?? null,
      signal: opts.signal ?? null,
      // Bearer no header, nunca cookie → allow_credentials=False no CORS do backend.
      credentials: 'omit',
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }

  if (!res.ok) {
    const err = await apiErrorFromResponse(res)
    if (err.status === 401) onUnauthorized()
    throw err
  }

  // 204: DELETE (soft delete). Sem corpo.
  if (res.status === 204) {
    return { data: undefined as T, status: 204, created: false }
  }

  const data = (await res.json()) as T
  // res.ok cobre 200 E 201 — o replay NÃO é erro. Quem precisa distinguir
  // "criei agora" de "replay" lê `created`.
  return { data, status: res.status, created: res.status === 201 }
}

/** Atalho para quando o 200-vs-201 não importa (todo GET, e mutações sem toast distinto). */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return (await requestFull<T>(path, opts)).data
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, opts: Omit<RequestOptions, 'method'> = {}) =>
    requestFull<T>(path, { ...opts, method: 'POST' }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  // PUT = upsert idempotente (hoje só /v1/loan-imports/settings). requestFull
  // porque quem chama distingue 201 (criou) de 200 (atualizou) no toast.
  put: <T>(path: string, body: unknown) => requestFull<T>(path, { method: 'PUT', body }),
  // DELETE devolve 204 sem corpo; `undefined` em vez de `void` porque
  // `request<void>` dispara no-invalid-void-type (void não é um tipo de valor).
  del: (path: string) => request<undefined>(path, { method: 'DELETE' }),
} as const
